import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, X, Loader2, Tag, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API_URL = `${import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au'}/public/subscription`;

function formatSize(bytes: number) {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(0)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${bytes} B`;
}

const PLAN_HIGHLIGHTS: Record<string, string[]> = {
  trial: ['3 clients', '50 AI queries', '1 month free trial', 'No credit card required'],
  starter: ['10 clients', '500 AI queries/mo', '2 users', '200 documents', 'PDF/Word/CSV export'],
  professional: ['50 clients', '2,000 AI queries/mo', '5 users', '10,000 documents', 'Compliance suite', 'ATO benchmarks'],
  enterprise: ['200 clients', '10,000 AI queries/mo', '10 users', '20,000 documents', 'All intelligence features', 'Multi-client queries'],
  unlimited: ['500 clients', '50,000 AI queries/mo', '20 users', '50,000 documents', 'All features included', 'API access'],
};

const PLAN_COLORS: Record<string, string> = {
  trial: 'border-zinc-700',
  starter: 'border-[#5048e5]/30',
  professional: 'border-indigo-500/50 ring-1 ring-indigo-500/20',
  enterprise: 'border-purple-500/50 ring-1 ring-purple-500/20',
  unlimited: 'border-yellow-500/30',
};

// Plan tiers: trial=0, starter=1, professional=2, enterprise=3, unlimited=4
// Feature grid: name + minimum plan tier required
const FEATURE_GRID: { name: string; minPlan: number }[] = [
  // Row 1-2: Foundation & Export
  { name: 'AI Document Chat', minPlan: 0 },
  { name: 'Document Upload & Indexing', minPlan: 0 },
  { name: 'PDF Export', minPlan: 0 },
  { name: 'Word Export', minPlan: 0 },
  { name: 'CSV Export', minPlan: 0 },
  { name: 'Excel Export', minPlan: 1 },
  // Row 2
  { name: 'Pie & Donut Charts', minPlan: 0 },
  { name: 'Bar & Line Charts', minPlan: 0 },
  { name: 'Waterfall Charts', minPlan: 1 },
  { name: 'Stacked Bar Charts', minPlan: 1 },
  { name: 'Chat History & Sessions', minPlan: 0 },
  { name: 'Markdown & Table Rendering', minPlan: 0 },
  // Row 3: Xero & Data
  { name: 'Xero Organisation Connection', minPlan: 1 },
  { name: 'Xero P&L Reports', minPlan: 1 },
  { name: 'Xero Balance Sheet', minPlan: 1 },
  { name: 'Xero Trial Balance', minPlan: 2 },
  { name: 'Xero Aged Receivables', minPlan: 2 },
  { name: 'ABN Auto-Matching', minPlan: 1 },
  // Row 4: Compliance
  { name: 'ATO Compliance Checker', minPlan: 2 },
  { name: 'ATO Audit Risk Predictor', minPlan: 3 },
  { name: 'ATO Industry Benchmarks (95)', minPlan: 2 },
  { name: 'Deadline Risk Alerts', minPlan: 2 },
  { name: 'Division 7A Checker', minPlan: 2 },
  { name: 'BAS Validator', minPlan: 2 },
  // Row 5: Tax & Revenue
  { name: 'What-If Scenario Modelling', minPlan: 3 },
  { name: 'Tax Opportunity Scanner', minPlan: 3 },
  { name: 'Year-End Tax Planning', minPlan: 3 },
  { name: 'Superannuation Compliance', minPlan: 3 },
  { name: 'FBT Detector', minPlan: 3 },
  { name: 'CGT Calculator', minPlan: 4 },
  // Row 6: Reports & Dashboards
  { name: 'Auto-Generated Client Reports', minPlan: 2 },
  { name: '8 Report Templates', minPlan: 2 },
  { name: 'Firm-Wide Dashboard', minPlan: 2 },
  { name: 'Client Health Score', minPlan: 2 },
  { name: 'Dashboard Widgets', minPlan: 3 },
  { name: 'Interactive Charts', minPlan: 3 },
  // Row 7: Intelligence
  { name: 'Multi-Client Queries', minPlan: 3 },
  { name: 'Cross-Client Anomaly Detection', minPlan: 3 },
  { name: 'Business Failure Warning', minPlan: 3 },
  { name: 'Multi-Year Trend Analysis', minPlan: 3 },
  { name: 'Financial Ratio Tracking', minPlan: 3 },
  { name: 'Industry Benchmarking', minPlan: 3 },
  // Row 8: Predictive
  { name: 'Cash Flow Forecasting', minPlan: 3 },
  { name: 'Predictive Analytics', minPlan: 4 },
  { name: 'Crystal Ball Dashboard', minPlan: 4 },
  { name: 'Client Churn Prediction', minPlan: 3 },
  { name: 'Dividend/Distribution Optimizer', minPlan: 3 },
  { name: 'Business Valuation Estimator', minPlan: 4 },
  // Row 9: Document Intelligence
  { name: 'Smart Document Comparison', minPlan: 2 },
  { name: 'Auto-Extract & Categorize', minPlan: 3 },
  { name: 'Receipt/Invoice OCR', minPlan: 3 },
  { name: 'Missing Document Detector', minPlan: 3 },
  { name: 'Contract/Lease Analyser', minPlan: 4 },
  { name: 'Document Version Control', minPlan: 4 },
  // Row 10: Automation
  { name: 'Auto-Reconciliation Checker', minPlan: 2 },
  { name: 'AI Client Onboarding', minPlan: 3 },
  { name: 'Workflow Templates', minPlan: 3 },
  { name: 'Smart Reminders', minPlan: 3 },
  { name: 'Automated Journal Entries', minPlan: 4 },
  { name: 'Staff Assignment Engine', minPlan: 4 },
  // Row 11: Client Portal
  { name: 'Client Portal with AI Chat', minPlan: 3 },
  { name: 'Secure Document Request', minPlan: 3 },
  { name: 'Client Task Board', minPlan: 3 },
  { name: 'Client Lifecycle Management', minPlan: 3 },
  { name: 'Client Mobile App', minPlan: 4 },
  { name: 'Client Satisfaction Tracking', minPlan: 4 },
  // Row 12: Integrations
  { name: 'Google Drive Sync', minPlan: 1 },
  { name: 'OneDrive Sync', minPlan: 1 },
  { name: 'Multi-Org Xero (Partner API)', minPlan: 4 },
  { name: 'MYOB Integration', minPlan: 4 },
  { name: 'QuickBooks Integration', minPlan: 3 },
  { name: 'ATO Direct Integration', minPlan: 4 },
  // Row 13: Security
  { name: 'AES-256 Encryption', minPlan: 0 },
  { name: 'Tenant Isolation', minPlan: 0 },
  { name: 'Role-Based Access', minPlan: 1 },
  { name: 'Audit Trail', minPlan: 3 },
  { name: 'Two-Factor Authentication', minPlan: 3 },
  { name: 'SOC 2 Compliance', minPlan: 4 },
  // Row 14: AI Enhancements
  { name: 'Data Source Selector', minPlan: 0 },
  { name: 'Benchmark Citations', minPlan: 1 },
  { name: 'Citation Links', minPlan: 2 },
  { name: 'Follow-Up Suggestions', minPlan: 2 },
  { name: 'Voice Input/Output', minPlan: 4 },
  { name: 'Multi-Language Support', minPlan: 4 },
  // Row 15: Platform
  { name: 'Custom Report Builder', minPlan: 3 },
  { name: 'Batch Report Generation', minPlan: 3 },
  { name: 'Usage Analytics Dashboard', minPlan: 2 },
  { name: 'Conversation Sharing', minPlan: 2 },
  { name: 'API Access', minPlan: 4 },
  { name: 'White-Label Branding', minPlan: 4 },
];

const PLAN_TIERS: Record<string, number> = { trial: 0, starter: 1, professional: 2, enterprise: 3, unlimited: 4 };

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firmName, setFirmName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState<any>(null);
  const [step, setStep] = useState(1); // 1: plan, 2: details

  const cancelled = searchParams.get('checkout') === 'cancelled';

  // Fetch plans
  const { data: plans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/plans`);
      const d = await r.json();
      return d.data;
    },
  });

  // Validate coupon
  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const r = await fetch(`${API_URL}/validate-coupon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode, planName: selectedPlan }),
      });
      const d = await r.json();
      setCouponValid(d.data);
      if (d.data?.valid) toast.success(`Coupon applied: ${d.data.description}`);
      else toast.error(d.data?.message || 'Invalid coupon');
    } catch { toast.error('Failed to validate coupon'); }
  };

  // Create account / checkout
  const checkoutMutation = useMutation({
    mutationFn: async () => {
      if (!email || !password) throw new Error('Email and password required');
      if (password.length < 8) throw new Error('Password must be at least 8 characters');

      if (selectedPlan === 'trial') {
        // Free trial — create account directly
        const r = await fetch(`${API_URL}/signup`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, firmName, planName: 'trial' }),
        });
        const d = await r.json();
        if (!d.success) throw new Error(d.error);
        return { ...d.data, redirect: `/login` };
      } else {
        // Paid plan — create account first, then redirect to Stripe
        const signupR = await fetch(`${API_URL}/signup`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, firmName, planName: selectedPlan }),
        });
        const signupD = await signupR.json();
        if (!signupD.success) throw new Error(signupD.error);

        // Now create Stripe checkout
        const r = await fetch(`${API_URL}/create-checkout`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planName: selectedPlan, email, firmName, billingCycle, couponCode: couponValid?.valid ? couponCode : undefined }),
        });
        const d = await r.json();
        if (!d.success) throw new Error(d.error);
        return { ...d.data, redirect: d.data.checkoutUrl };
      }
    },
    onSuccess: (data) => {
      if (data.redirect?.startsWith('http')) {
        toast.success('Account created! Redirecting to payment...');
        setTimeout(() => { window.location.href = data.redirect; }, 1000);
      } else {
        toast.success('Account created! Please log in.');
        setTimeout(() => { window.location.href = data.redirect || '/login'; }, 1500);
      }
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create account'),
  });

  const selectedPlanData = (plans || []).find((p: any) => p.name === selectedPlan);
  const price = selectedPlanData ? parseFloat(billingCycle === 'yearly' ? selectedPlanData.price_yearly : selectedPlanData.price_monthly) || 0 : 0;
  const monthlyEquivalent = billingCycle === 'yearly' ? Math.round(price / 12) : price;
  const discount = couponValid?.valid && couponValid.discountPercent ? parseFloat(couponValid.discountPercent) : 0;
  const finalPrice = discount > 0 ? Math.round(price * (1 - discount / 100)) : price;

  return (
    <div className="min-h-screen bg-[#262635] text-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-12 h-20">
        <Link to="/" className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#5048e5]" />
          <span className="text-base tracking-[0.15em] font-light">RONIDOC<span className="font-semibold">AI</span></span>
        </Link>
        <Link to="/login" className="text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">Sign In</Link>
      </div>

      <div className="text-center pt-8 pb-8 px-6">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#5048e5]/60 mb-6">Get Started</p>
        <h1 className="text-[clamp(1.8rem,4vw,3rem)] font-extralight mb-3">
          {selectedPlanData ? (
            <>{selectedPlanData.display_name} <span className="text-zinc-600">|</span> {selectedPlan === 'trial' ? <span className="text-green-400">$0</span> : <span>${finalPrice}<span className="text-lg text-zinc-500">/{billingCycle === 'yearly' ? 'year' : 'month'}</span></span>}</>
          ) : 'Choose your plan'}
        </h1>
        <p className="text-sm text-zinc-500 max-w-lg mx-auto">
          {selectedPlan === 'trial' ? 'No credit card required. Free for 1 month.' : 'Start with a free trial or select a plan that fits your firm.'}
        </p>

        {cancelled && (
          <div className="mt-6 p-3 border border-yellow-500/20 rounded text-yellow-400/80 text-sm max-w-md mx-auto">
            Checkout was cancelled. Select a plan to try again.
          </div>
        )}
      </div>

      {step === 1 && (
        <>
          {/* Billing Toggle */}
          <div className="flex justify-center mb-10">
            <div className="border border-zinc-800 rounded-full p-1 flex gap-1">
              <button onClick={() => setBillingCycle('monthly')} className={cn('px-6 py-2 rounded-full text-xs tracking-wider uppercase transition-all duration-300', billingCycle === 'monthly' ? 'bg-[#5048e5] text-white' : 'text-zinc-500 hover:text-white')}>Monthly</button>
              <button onClick={() => setBillingCycle('yearly')} className={cn('px-6 py-2 rounded-full text-xs tracking-wider uppercase transition-all duration-300', billingCycle === 'yearly' ? 'bg-[#5048e5] text-white' : 'text-zinc-500 hover:text-white')}>
                Yearly <span className="text-[10px] text-green-400 ml-1">-20%</span>
              </button>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-[1px] bg-zinc-900/80 mb-12">
            {(plans || []).map((plan: any) => {
              const isSelected = selectedPlan === plan.name;
              const planPrice = parseFloat(billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
              const monthlyEq = billingCycle === 'yearly' ? Math.round(planPrice / 12) : planPrice;
              const highlights = PLAN_HIGHLIGHTS[plan.name] || [];
              const isPopular = plan.name === 'professional';

              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.name)}
                  className={cn(
                    'relative p-6 md:p-8 pb-8 cursor-pointer transition-all duration-500 bg-[#262635]',
                    isSelected ? 'ring-1 ring-[#5048e5]/30 bg-[#5048e5]/5' : 'hover:bg-zinc-900/50'
                  )}
                >
                  <div className="flex flex-col h-full">
                    <div className="h-5 mb-2">
                      {isPopular && (
                        <p className="text-[10px] tracking-[0.3em] uppercase text-[#5048e5]">Most Popular</p>
                      )}
                    </div>
                    <h3 className="text-lg font-medium">{plan.display_name}</h3>
                    <div className="my-4">
                      {parseFloat(plan.price_monthly) === 0 ? (
                        <span className="text-3xl font-extralight">Free</span>
                      ) : (
                        <>
                          <span className="text-4xl font-extralight">${monthlyEq}</span>
                          <span className="text-zinc-600 text-sm">/mo</span>
                          {billingCycle === 'yearly' && <p className="text-xs text-zinc-700 mt-1">${planPrice}/year</p>}
                        </>
                      )}
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <Check className="h-3 w-3 text-[#5048e5] flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-500">{h}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      className={cn(
                        'w-full mt-auto text-[12px] tracking-[0.15em] uppercase py-3.5 rounded-full transition-all duration-300',
                        isSelected ? 'bg-[#5048e5] hover:bg-[#5f58e8] text-white' : 'border border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white'
                      )}
                      onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan.name); setStep(2); }}
                    >
                      {plan.name === 'trial' ? 'Start Free' : 'Select'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature Comparison Grid */}
          <div className="px-6 md:px-12 lg:px-24 mb-12">
            <div className="text-center mb-10">
              <p className="text-[11px] tracking-[0.4em] uppercase text-[#9c86fd]/60 mb-4">Feature Comparison</p>
              <h3 className="text-2xl font-extralight">
                Everything included in <span className="font-medium text-white">{selectedPlan === 'trial' ? 'Free Trial' : selectedPlan === 'unlimited' ? 'Scale' : selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}</span>
              </h3>
              <p className="text-xs text-zinc-600 mt-2">
                {FEATURE_GRID.filter(f => f.minPlan <= (PLAN_TIERS[selectedPlan] ?? 0)).length} of {FEATURE_GRID.length} features available
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-[1px] bg-zinc-900/60 select-none">
              {FEATURE_GRID.map((feature, i) => {
                const currentTier = PLAN_TIERS[selectedPlan] ?? 0;
                const isActive = feature.minPlan <= currentTier;
                const tierNames = ['Free', 'Starter', 'Pro', 'Enterprise', 'Scale'];
                const minPlanLabel = tierNames[feature.minPlan] || '';

                return (
                  <div
                    key={i}
                    className={cn(
                      'relative p-3 md:p-4 transition-all duration-500 bg-[#262635] group',
                      isActive
                        ? 'hover:bg-[#5048e5]/[0.08]'
                        : 'opacity-40'
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <div className={cn(
                        'w-4 h-4 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-500',
                        isActive ? 'bg-[#5048e5]/20' : 'bg-zinc-800/40'
                      )}>
                        {isActive ? (
                          <Check className="h-2.5 w-2.5 text-[#7c75f0]" />
                        ) : (
                          <X className="h-2.5 w-2.5 text-zinc-700" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className={cn(
                          'text-[11px] leading-tight font-medium transition-colors duration-500',
                          isActive ? 'text-zinc-300' : 'text-zinc-700'
                        )}>{feature.name}</p>
                        {!isActive && (
                          <p className="text-[9px] text-zinc-700 mt-0.5">{minPlanLabel}+</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-center gap-6 mt-6">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-[#5048e5]/20 flex items-center justify-center"><Check className="h-2 w-2 text-[#7c75f0]" /></div><span className="text-[10px] text-zinc-500">Included</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-zinc-800/40 flex items-center justify-center"><X className="h-2 w-2 text-zinc-700" /></div><span className="text-[10px] text-zinc-500">Upgrade required</span></div>
            </div>

            <div className="text-center mt-8">
              <button
                onClick={() => setStep(2)}
                className="bg-[#5048e5] hover:bg-[#5f58e8] text-white text-[12px] tracking-[0.15em] uppercase px-10 py-4 rounded-full transition-all duration-300"
              >
                Continue with {selectedPlan === 'trial' ? 'Free Trial' : selectedPlan === 'unlimited' ? 'Scale' : selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}
              </button>
            </div>
          </div>
        </>
      )}

      {step === 2 && (
        <div className="max-w-md mx-auto px-6 pb-16">
          <button onClick={() => setStep(1)} className="text-xs tracking-[0.15em] uppercase text-zinc-600 hover:text-white mb-8 transition">
            ← Back to plans
          </button>

          <div className="border border-zinc-900 p-8">
            <h2 className="text-xl font-extralight mb-1">Complete your signup</h2>
            <p className="text-xs text-zinc-600 mb-8">
              {selectedPlanData?.display_name} | {selectedPlan === 'trial' ? 'Free for 1 month · No credit card required' : `$${finalPrice}/${billingCycle === 'yearly' ? 'year' : 'month'}`}
              {discount > 0 && <span className="text-green-400 ml-1">({discount}% off)</span>}
            </p>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-[11px] tracking-[0.15em] uppercase text-zinc-600">Email</Label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yourfirm.com.au" className="bg-transparent border-zinc-800 text-white focus:border-zinc-600 rounded-none h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-[11px] tracking-[0.15em] uppercase text-zinc-600">Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" className="bg-transparent border-zinc-800 text-white focus:border-zinc-600 rounded-none h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-[11px] tracking-[0.15em] uppercase text-zinc-600">Firm Name</Label>
                <Input value={firmName} onChange={e => setFirmName(e.target.value)} placeholder="Your Accounting Firm Pty Ltd" className="bg-transparent border-zinc-800 text-white focus:border-zinc-600 rounded-none h-11" />
              </div>

              {selectedPlan !== 'trial' && (
                <div className="space-y-2">
                  <Label className="text-[11px] tracking-[0.15em] uppercase text-zinc-600">Promo Code (optional)</Label>
                  <div className="flex gap-2">
                    <Input value={couponCode} onChange={e => { setCouponCode(e.target.value); setCouponValid(null); }} placeholder="LAUNCH40" className="bg-transparent border-zinc-800 text-white focus:border-zinc-600 rounded-none h-11 uppercase flex-1" />
                    <button onClick={validateCoupon} className="text-[11px] tracking-[0.15em] uppercase text-zinc-500 hover:text-white border border-zinc-800 hover:border-zinc-600 px-4 h-11 transition">
                      Apply
                    </button>
                  </div>
                  {couponValid?.valid && (
                    <p className="text-xs text-green-400">{couponValid.description} — {couponValid.discountPercent}% off for {couponValid.durationMonths} month{couponValid.durationMonths > 1 ? 's' : ''}</p>
                  )}
                  {couponValid && !couponValid.valid && (
                    <p className="text-xs text-red-400">{couponValid.message}</p>
                  )}
                </div>
              )}

              {/* Price Summary */}
              {selectedPlan !== 'trial' && (
                <div className="border border-zinc-900 p-4 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-600">{selectedPlanData?.display_name} ({billingCycle})</span>
                    <span className="text-zinc-400">${price.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-green-400/70">Discount ({discount}%)</span>
                      <span className="text-green-400/70">-${(price - finalPrice).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-medium border-t border-zinc-900 pt-2">
                    <span className="text-zinc-300">Total</span>
                    <span className="text-white">${finalPrice.toFixed(2)} AUD</span>
                  </div>
                </div>
              )}

              <button
                className="w-full h-12 bg-[#5048e5] hover:bg-[#5f58e8] text-[12px] tracking-[0.15em] uppercase rounded-full transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                onClick={() => checkoutMutation.mutate()}
                disabled={!email || !password || password.length < 8 || checkoutMutation.isPending}
              >
                {checkoutMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {selectedPlan === 'trial' ? 'Start Free Trial' : 'Continue to Payment'}
              </button>

              <p className="text-center text-[11px] text-zinc-700 mt-4">
                {selectedPlan === 'trial' ? 'No credit card required. Free for 1 month.' : 'Secure payment via Stripe. Cancel anytime.'}
              </p>

              <p className="text-center text-xs text-zinc-600 mt-6">
                Already have an account?{' '}
                <Link to="/login" className="text-[#5048e5] hover:text-[#6b64ef] transition">Sign in</Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-24 py-10 border-t border-zinc-900 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#7c75f0]" />
          <span className="text-xs tracking-[0.15em] font-light text-zinc-600">RONIDOC<span className="font-semibold">AI</span></span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/terms" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Terms</Link>
          <Link to="/privacy-policy" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Privacy</Link>
        </div>
        <p className="text-[11px] text-zinc-800">&copy; {new Date().getFullYear()} RoniDoc AI</p>
      </footer>
    </div>
  );
}
